import React, { FC, memo, useEffect, useState, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { getTimeWithTimeZone, timezone } from 'utils/helpers/momentTime';
import { Droppable } from 'react-beautiful-dnd';
import { IEvents } from 'types';
import DatePicker from 'react-datepicker';
import CustomButton from 'components/common/customButton/customButton';
import _ from 'lodash';
import { Scrollbars } from 'react-custom-scrollbars';
import TimeLine from 'components/common/calendar/timeLine';
import arrow from 'style/imgs/icons/arrow.svg';
import styles from 'components/common/calendar/index.module.scss';
import calendarIcon from 'style/imgs/icons/Calendar.svg';
import buttons from 'style/buttons.module.scss';
import Event from './events';
import scheduleStyles from '../index.module.scss';

interface IScheduleAnetapack {
  events?: IEvents[];
  anetaPack?: IEvents[];
  errortimeEvents?: IEvents[];
  anetaPackSchedule?: IEvents[];
  startDate?: string;
  currentKidName?: string;
  anetaPackName?: string;
  setSelectedDate?: React.Dispatch<React.SetStateAction<Moment>>;
  getVisibleEvents?: (e: IEvents[]) => void;
  dndId: number;
  searcIndex: (inputArray: Moment[][], searchValue: string) => number[] | null;
  removeEventFromSchedule?: (eventId: number) => void;
  handleMoveAll?: () => void;
}
const ScheduleAnetapack: FC<IScheduleAnetapack> = ({
  events,
  searcIndex,
  anetaPack,
  dndId,
  removeEventFromSchedule,
  currentKidName,
  handleMoveAll,
  errortimeEvents,
  anetaPackSchedule,
  setSelectedDate,
  startDate,
  anetaPackName,
  getVisibleEvents,
}) => {
  const [timeLine, setTimeLine] = useState<string[]>([]);
  const [week, setWeeks] = useState(0);
  const [days, setDays] = useState<Moment[][]>([]);
  const [startDay, setStartDay] = useState(
    moment().clone().startOf('year').startOf('week')
  );
  const [lastDay, setLastDay] = useState(
    moment().clone().endOf('year').endOf('week')
  );
  const [displayDate, setDisplayDate] = useState('');
  const [selectDate, setSelectDate] = useState(
    startDate ? moment(+startDate).toDate() : moment().toDate()
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dayFormat = dndId === 1 ? 'YYYY-MM-DD' : 'ddd';

  const errorDates = useMemo(() => {
    return _.map(errortimeEvents, (el: IEvents) => {
      return moment(el.date).toDate();
    });
  }, [errortimeEvents]);

  useEffect(() => {
    const day = startDay.clone().subtract(1, 'day');
    const calendar = [];

    while (day.isBefore(lastDay, 'day')) {
      calendar.push(
        Array(1)
          .fill(0)
          .map(() => day.add(1, 'day').clone())
      );
    }
    const date = startDate
      ? moment(+startDate).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    const currentWeek = searcIndex(calendar, date);
    if (currentWeek) {
      setWeeks(currentWeek[0]);
    }
    setDays(calendar);
  }, [lastDay, searcIndex, startDay, startDate]);

  useEffect(() => {
    setDisplayDate(
      `${moment(days[week] && days[week][0]).format(
        `${dndId === 1 ? 'ddd, MMMM DD, YYYY' : 'dddd'}`
      )} `
    );
    if (getVisibleEvents && anetaPack && days && week) {
      const filteredEvents: IEvents[] = anetaPack.filter((el) => {
        if (
          days[week][0].format(dayFormat) ===
          moment(el.startTime).add(timezone, 'minutes').format(dayFormat)
        ) {
          return el;
        }
        return null;
      });
      getVisibleEvents(filteredEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, week, dndId]);

  useEffect(() => {
    if (setSelectedDate) {
      setSelectedDate(moment(days[week] && days[week][0]));
    }
  }, [days, week, setSelectedDate]);

  useEffect(() => {
    if (days && !_.isEmpty(days) && searcIndex) {
      const currentWeek = searcIndex(
        days,
        moment(selectDate).format('YYYY-MM-DD')
      );
      if (currentWeek) {
        setWeeks(currentWeek[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDate, days]);

  const renderBlocks = (
    event: IEvents,
    timeStamp: string,
    day: moment.Moment,
    idx: number
  ) => {
    let render = null;
    const startTime = getTimeWithTimeZone(
      event.startTimeStr || '00:00',
      'HH:mm'
    );
    const eventTime = moment(
      `${moment(startTime, 'HH:mm').format('LT')} ${moment(day).format(
        'MMM, D'
      )}`
    );
    const position = moment(`${timeStamp} ${moment(day).format('MMM, D')}`);
    const diff = eventTime.diff(position, 'minutes');

    if (
      startTime === moment(timeStamp, 'LT').format('HH:mm') ||
      (diff <= 14 && diff > 0)
    ) {
      if (
        day.format(dayFormat) ===
        moment(event.startTime).add(timezone, 'minutes').format(dayFormat)
      ) {
        render = (
          <Event
            idx={idx}
            dndId={dndId}
            diff={diff}
            key={event.id}
            day={day}
            event={event}
            removeEventFromSchedule={removeEventFromSchedule}
            anetaPackSchedule={anetaPackSchedule}
            errortimeEvents={errortimeEvents}
          />
        );
      }
    }
    return render;
  };

  const handleNext = () => {
    if (dndId === 2 && week === 6) {
      return;
    }
    if (week < days.length - 1) {
      setWeeks(week + 1);
    }
    if (days.length - 1 === week) {
      setLastDay(lastDay.clone().add(1, 'month'));
    }
  };

  const handlePrev = () => {
    if (week >= 1) {
      setWeeks(week - 1);
    }
    if (week === 0) {
      if (dndId === 2) {
        return;
      }
      setStartDay(startDay?.clone().subtract(1, 'month'));
    }
  };

  const handleChangeCalendarDay = (d: Date) => {
    if (days && !_.isEmpty(days) && searcIndex && isCalendarOpen) {
      const lastDayInArray: moment.Moment | undefined = _.last(_.last(days));
      const dayIndex = searcIndex(days, moment(d).format('YYYY-MM-DD'));

      if (dayIndex && week !== dayIndex[0]) {
        setWeeks(dayIndex[0]);
      }
      const diff = moment(d).diff(moment(lastDayInArray), 'months');
      if (dayIndex === null) {
        if (diff > 0) {
          setLastDay(lastDay.clone().add(diff + 1, 'month'));
        } else {
          setStartDay(startDay.clone().subtract(diff * -1, 'month'));
        }
      }
    }
    setSelectDate(d);
  };
  return (
    <>
      <div className={scheduleStyles.scheduleHeader}>
        <span className="message-title">
          {anetaPack
            ? `Shared Anetapack
                ${anetaPackName}'s Schedule`
            : `${currentKidName}'s Anetapack Schedule`}
        </span>
        <div
          className={`${styles.switchDaysButtons} ${scheduleStyles.calendarButton}`}
        >
          <button
            onClick={handlePrev}
            type="button"
            className={styles.buttonNext}
          >
            <img src={arrow} alt="prev" />
          </button>
          {anetaPack ? (
            <CustomButton
              width={100}
              color="orange"
              text={displayDate}
              icon={calendarIcon}
              disableCalendar
            />
          ) : (
            <DatePicker
              selected={selectDate}
              onChange={handleChangeCalendarDay}
              highlightDates={errorDates}
              disabledKeyboardNavigation
              dateFormat="d MMM, yyyy"
              onCalendarClose={() => setIsCalendarOpen(false)}
              onCalendarOpen={() => setIsCalendarOpen(true)}
              customInput={
                <CustomButton
                  width={dndId === 1 ? 185 : 140}
                  color="orange"
                  text={displayDate}
                  icon={calendarIcon}
                />
              }
            />
          )}
          <button
            onClick={handleNext}
            type="button"
            className={styles.buttonNext}
          >
            <img src={arrow} alt="next" />
          </button>
        </div>
        {handleMoveAll && (
          <button
            type="button"
            className={`${buttons.buttomPrimary} ${scheduleStyles.buttonMoveAll}`}
            onClick={handleMoveAll}
          >
            Move All
          </button>
        )}
      </div>
      <Scrollbars autoHeight autoHeightMax={420}>
        <div className={scheduleStyles.tableBlock}>
          <TimeLine cellSize="S" onChange={setTimeLine} timeLine={timeLine} />
          <div className={`${styles.days} ${scheduleStyles.days}`}>
            {!_.isEmpty(days) &&
              !_.isEmpty(days[week]) &&
              days[week].map((day: moment.Moment) => (
                <div
                  key={day.format('DD-MM-YYYY')}
                  style={{ width: '100%' }}
                  className={styles.day}
                >
                  {timeLine.map((timeStamp: string) => (
                    <Droppable
                      droppableId={`${dndId}_${timeStamp}_${day.format(
                        'YYYY-MM-DD'
                      )}`}
                      key={timeStamp}
                    >
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={styles.timeLine}
                          style={{
                            transition: 'all 0.1s ease-in',
                            background: snapshot.isDraggingOver
                              ? '#f0f4f5'
                              : '#fff',
                          }}
                        >
                          {events && !_.isEmpty(events)
                            ? events.map((e: IEvents, i: number) =>
                                renderBlocks(e, timeStamp, day, i)
                              )
                            : anetaPack?.map((e: IEvents, i: number) =>
                                renderBlocks(e, timeStamp, day, i)
                              )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>
              ))}
          </div>
        </div>
      </Scrollbars>
    </>
  );
};

export default memo(ScheduleAnetapack);
